import React, { useRef, useState, useEffect, useCallback } from "react";

// Environment flag to show/hide analysis panel (defaults to false for production)
const SHOW_ANALYSIS_PANEL = import.meta.env.VITE_SHOW_ANALYSIS_PANEL === "true";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useMobileOptimizations } from "../../hooks/useMobileOptimizations";
import { Panel, DefaultButton, TextField, SpinButton, Slider, Checkbox, PanelType } from "@fluentui/react";
import { useUser, useAuth } from "@clerk/clerk-react";
import { CopilotSelector } from "../../components/CopilotSelector";
import { ContextModal } from "../../components/ContextModal";
import { UpgradePlanModal } from "../../components/UpgradePlanModal";
import { CopilotInfo } from "../../components/CopilotIndicator";
import { ChatHistory } from "../../components/ChatHistory";

import styles from "./Chat.module.css";

import { RetrievalMode, RAGChatCompletion, RAGChatCompletionDelta, ChatAppRequestOptions, carSearchService, chatHistoryService, ConversationWithMessages } from "../../api";
import { savedCarsService } from "../../api/savedCarsService";
import { AIChatProtocolClient, AIChatMessage } from "@microsoft/ai-chat-protocol";
import { Answer, AnswerError, AnswerLoading, QuotaExceededMessage } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { VectorSettings } from "../../components/VectorSettings";
import { ResultsPanel } from "../../components/ResultsPanel";
import { CarDetailDrawer } from "../../components/CarDetailDrawer";
import { Sidebar } from "../../components/Sidebar";
import { ResizableSplitter } from "../../components/ResizableSplitter";
import { SavedCars } from "../../components/SavedCars/SavedCars";
import { Oportunidades } from "../../components/Oportunidades";
import MobileToggle from "../../components/MobileToggle";
import MobileSidebarToggle from "../../components/MobileSidebarToggle";
import { ComparisonFAB, CarComparisonView } from "../../components/CarComparison";

// Copilot definitions
const COPILOTS: CopilotInfo[] = [
    {
        id: "firulai",
        name: "FirulAI",
        role: "El Sabueso Experto",
        avatar: "/firulAI_copilot.png"
    },
    {
        id: "don-trato",
        name: "Don Trato",
        role: "El Genio Reseller del Mercado Nica",
        avatar: "/revendedor.png"
    },
    {
        id: "profe-ahorro",
        name: "Profe Ahorro",
        role: "La Maga de los Números",
        avatar: "/genio_num.png"
    },
    {
        id: "pofi",
        name: "El Pofi",
        role: "El Fanático de los Carros (moridor)",
        avatar: "/elpofi.png"
    }
];

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isCopilotSelectorOpen, setIsCopilotSelectorOpen] = useState(false);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const [userContext, setUserContext] = useState<string>("");
    const [isSavedCarsOpen, setIsSavedCarsOpen] = useState(false);
    const [isOportunidadesOpen, setIsOportunidadesOpen] = useState(false);
    const [savedCarsCount, setSavedCarsCount] = useState(0);
    const [savedCarIds, setSavedCarIds] = useState<Set<string>>(new Set());
    const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(undefined);
    const [selectedCopilotId, setSelectedCopilotId] = useState<string | undefined>(() => {
        // Load from localStorage on mount, default to FirulAI if not set
        return localStorage.getItem('selectedCopilotId') || 'firulai';
    });
    const { user } = useUser();
    const { getToken } = useAuth();

    // Generate session ID once per chat session for conversation context tracking
    const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
    
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [retrieveCount, setRetrieveCount] = useState<number>(15);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useAdvancedFlow, setUseAdvancedFlow] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const currentSessionStateRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [isQuotaExceeded, setIsQuotaExceeded] = useState<boolean>(false);

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: RAGChatCompletion][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: RAGChatCompletion][]>([]);

    // Car listings state
    const [carListings, setCarListings] = useState<any[]>([]);
    const [isSearchingCars, setIsSearchingCars] = useState(false);
    const [selectedCar, setSelectedCar] = useState<any>(null);
    const [searchSummary, setSearchSummary] = useState<string>("");
    const [isCarDetailOpen, setIsCarDetailOpen] = useState(false);
    
    
    // Mobile UI state
    const [showMobileResults, setShowMobileResults] = useState(false);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    // Comparison state
    const [selectedCarsForComparison, setSelectedCarsForComparison] = useState<string[]>([]);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);

    // Subscription and quota state
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [showLimitReached, setShowLimitReached] = useState(false);
    const [quotaInfo, setQuotaInfo] = useState<{
        queriesUsed: number;
        queriesLimit: number | null;
        isUnlimited: boolean;
        canQuery: boolean;
        planId?: string;
    }>({ queriesUsed: 0, queriesLimit: 20, isUnlimited: false, canQuery: true });
    const [justUpgraded, setJustUpgraded] = useState(false);
    const [isPollingForUpgrade, setIsPollingForUpgrade] = useState(false);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Save default FirulAI selection to localStorage if not set
    useEffect(() => {
        if (!localStorage.getItem('selectedCopilotId')) {
            localStorage.setItem('selectedCopilotId', 'firulai');
            console.log('Default copilot set to FirulAI');
        }
    }, []);

    // Set user ID for all API requests when user is available
    useEffect(() => {
        if (user?.id && getToken) {
            console.log('Setting user ID for API requests:', user.id);
            carSearchService.setUserId(user.id);
            chatHistoryService.setUserId(user.id);
            savedCarsService.setUserId(user.id);

            // Set up global fetch interceptor to add auth header to all requests
            const originalFetch = window.fetch;
            window.fetch = async (input, init) => {
                // Only add header to API requests (not external resources)
                const url = typeof input === 'string'
                    ? input
                    : input instanceof Request
                        ? input.url
                        : input.toString();
                const isApiRequest = url.startsWith('/') || url.includes(window.location.host);

                if (isApiRequest) {
                    // Get the Clerk session token for authenticated requests
                    let token: string | null = null;
                    try {
                        token = await getToken();
                    } catch (e) {
                        console.warn('Failed to get auth token:', e);
                    }

                    // Merge headers with Authorization and user ID
                    const headers = new Headers(init?.headers);
                    if (token) {
                        headers.set('Authorization', `Bearer ${token}`);
                    }
                    // Keep x-clerk-user-id for backwards compatibility during transition
                    headers.set('x-clerk-user-id', user.id);

                    return originalFetch(input, {
                        ...init,
                        headers: headers
                    });
                }

                // External requests - don't modify
                return originalFetch(input, init);
            };

            // Cleanup: restore original fetch on unmount or user change
            return () => {
                window.fetch = originalFetch;
            };
        }
    }, [user, getToken]);

    // Microsoft Clarity: Identify user and set custom tags
    useEffect(() => {
        if (user?.id && typeof clarity !== "undefined") {
            // Identify user in Clarity for session tracking
            clarity("identify", user.id);

            // Set custom tags for filtering in Clarity dashboard
            clarity("set", "language", "es");
        }
    }, [user?.id]);

    // Microsoft Clarity: Update plan tag when subscription changes
    useEffect(() => {
        if (typeof clarity !== "undefined") {
            const planType = quotaInfo.isUnlimited ? "premium" : "free";
            clarity("set", "plan", planType);
        }
    }, [quotaInfo.isUnlimited]);

    // Update saved cars count and IDs on mount and when saved cars change
    useEffect(() => {
        const updateSavedCarsData = async () => {
            try {
                // Fetch both count and IDs in parallel
                const [count, ids] = await Promise.all([
                    savedCarsService.getSavedCarsCount(),
                    savedCarsService.getSavedCarIds()
                ]);
                setSavedCarsCount(count);
                setSavedCarIds(new Set(ids));
            } catch (error) {
                console.error("Error fetching saved cars data:", error);
                setSavedCarsCount(0);
                setSavedCarIds(new Set());
            }
        };

        updateSavedCarsData();

        // Listen for custom event when saved cars are updated
        const handleSavedCarsUpdate = () => updateSavedCarsData();
        window.addEventListener('savedCarsUpdated', handleSavedCarsUpdate);

        return () => {
            window.removeEventListener('savedCarsUpdated', handleSavedCarsUpdate);
        };
    }, []);


    // Fetch quota status on mount and after each query
    const fetchQuotaStatus = useCallback(async (checkForUpgrade = false) => {
        if (!user?.id) return;

        try {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await fetch('/api/subscription/quota', {
                headers: {
                    'x-clerk-user-id': user.id,
                    'x-user-timezone': userTimezone
                }
            });

            if (response.ok) {
                const data = await response.json();
                const wasUnlimited = quotaInfo.isUnlimited;
                const isNowUnlimited = data.is_unlimited;

                setQuotaInfo({
                    queriesUsed: data.queries_used_today,
                    queriesLimit: data.queries_limit,
                    isUnlimited: data.is_unlimited,
                    canQuery: data.can_query,
                    planId: data.plan_id
                });

                // Check if user just upgraded (was not unlimited, now is unlimited)
                if (checkForUpgrade && !wasUnlimited && isNowUnlimited) {
                    console.log('Upgrade detected! Stopping polling.');
                    setJustUpgraded(true);
                    stopPollingForUpgrade();
                    // Hide the "new" indicator after 30 seconds
                    setTimeout(() => setJustUpgraded(false), 30000);
                    return true; // Indicate upgrade was detected
                }
            }
        } catch (error) {
            console.error('Error fetching quota status:', error);
        }
        return false;
    }, [user?.id, quotaInfo.isUnlimited]);

    // Start polling for upgrade after checkout
    const startPollingForUpgrade = useCallback(() => {
        if (pollingIntervalRef.current) return; // Already polling

        console.log('Starting to poll for subscription upgrade...');
        setIsPollingForUpgrade(true);

        // Poll every 3 seconds for up to 2 minutes
        let pollCount = 0;
        const maxPolls = 40; // 40 * 3 seconds = 2 minutes

        pollingIntervalRef.current = setInterval(async () => {
            pollCount++;
            console.log(`Polling for upgrade... (${pollCount}/${maxPolls})`);

            const upgraded = await fetchQuotaStatus(true);

            if (upgraded || pollCount >= maxPolls) {
                stopPollingForUpgrade();
                if (!upgraded && pollCount >= maxPolls) {
                    console.log('Polling timeout - no upgrade detected');
                }
            }
        }, 3000);
    }, [fetchQuotaStatus]);

    // Stop polling
    const stopPollingForUpgrade = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setIsPollingForUpgrade(false);
    }, []);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        fetchQuotaStatus();
    }, [user]);

    // Handle upgrade button click
    const handleUpgradeClick = () => {
        setShowLimitReached(false);
        setIsUpgradeModalOpen(true);
    };

    // Handle FastSpring checkout using Store Builder Library
    const handleUpgrade = () => {
        // Access the global fastspring builder object
        const fastspring = (window as unknown as { fastspring?: { builder?: {
            push: (session: Record<string, unknown>) => void;
            checkout: () => void;
            reset: () => void;
        } } }).fastspring;

        if (fastspring?.builder) {
            // Reset any previous session
            fastspring.builder.reset();

            // Configure checkout session with clerk_user_id tag
            fastspring.builder.push({
                products: [{ path: 'videntis-suscripcion', quantity: 1 }],
                tags: {
                    clerk_user_id: user?.id || ''
                }
            });

            // Open popup checkout
            fastspring.builder.checkout();

            // Start polling for subscription update after a short delay
            // This gives the user time to complete the checkout
            setTimeout(() => {
                startPollingForUpgrade();
            }, 5000); // Start polling 5 seconds after opening checkout
        } else {
            // Fallback to direct URL if Store Builder not loaded
            console.warn('FastSpring Store Builder not loaded, using fallback URL');
            const fastspringStoreUrl = 'https://videntis.test.onfastspring.com/videntis-suscripcion';
            window.open(fastspringStoreUrl, '_blank');

            // Also start polling for fallback
            setTimeout(() => {
                startPollingForUpgrade();
            }, 5000);
        }

        setIsUpgradeModalOpen(false);
    };

    // Device detection and mobile optimizations
    const { isMobile, isTouchDevice } = useDeviceDetection();
    useMobileOptimizations({
        enableImageOptimization: true,
        enableScrollOptimization: true,
        enableMemoryManagement: true,
        enableBatteryOptimization: true,
    });

    const handleAsyncRequest = async (question: string, answers: [string, RAGChatCompletion][], result: AsyncIterable<RAGChatCompletionDelta>) => {
        let answer = "";
        let chatCompletion: RAGChatCompletion = {
            context: {
                data_points: {},
                followup_questions: null,
                thoughts: []
            },
            message: { content: "", role: "assistant" },
            sessionState: null // Initialize sessionState
        };
        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    // We need to create a new object to trigger a re-render
                    const latestCompletion: RAGChatCompletion = {
                        ...chatCompletion,
                        message: { content: answer, role: chatCompletion.message.role }
                    };
                    setStreamedAnswers([...answers, [question, latestCompletion]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            let chunkCount = 0;
            for await (const response of result) {
                chunkCount++;
                console.log(`Streaming chunk ${chunkCount}:`, response);
                
                if (response.context) {
                    console.log('Chunk has context with data_points:', response.context.data_points ? Object.keys(response.context.data_points).length : 0);
                    // Properly merge context, preserving data_points
                    chatCompletion.context = {
                        ...chatCompletion.context,
                        ...response.context,
                        data_points: {
                            ...chatCompletion.context.data_points,
                            ...(response.context.data_points || {})
                        }
                    };
                }
                // Handle sessionState from streaming response
                if (response.sessionState) {
                    console.log('Received sessionState in streaming response:', response.sessionState);
                    console.log('SessionState has last_search_results?', response.sessionState?.last_search_results?.length || 0);
                    console.log('SessionState last_intent:', response.sessionState?.last_intent);
                    chatCompletion.sessionState = response.sessionState;
                    currentSessionStateRef.current = response.sessionState;
                }
                if (response.delta && response.delta.role) {
                    chatCompletion.message.role = response.delta.role;
                }
                if (response.delta && response.delta.content) {
                    setIsLoading(false);
                    await updateState(response.delta.content);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        chatCompletion.message.content = answer;
        return chatCompletion;
    };

    const makeApiRequest = async (question: string) => {

        lastQuestionRef.current = question;

        error && setError(undefined);
        isQuotaExceeded && setIsQuotaExceeded(false);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        
        // Only show searching state if we don't have existing results or if this looks like a new search
        const hasExistingResults = carListings.length > 0;
        const looksLikeNewSearch = !currentSessionStateRef.current || 
                                  question.toLowerCase().includes('buscar') || 
                                  question.toLowerCase().includes('encontrar') || 
                                  question.toLowerCase().includes('mostrar');
        
        if (!hasExistingResults || looksLikeNewSearch) {
            setIsSearchingCars(true);
        }

        try {
            const messages: AIChatMessage[] = answers.flatMap(answer => [
                { content: answer[0], role: "user" },
                { content: answer[1].message.content, role: "assistant" }
            ]);
            const allMessages: AIChatMessage[] = [...messages, { content: question, role: "user" }];
            // Show username only on first message
            const isFirstMessage = answers.length === 0;
            const usernameToSend = isFirstMessage ? (user?.firstName || user?.username || undefined) : undefined;
            
            // Get session state from last answer or ref
            let sessionStateToSend = currentSessionStateRef.current;
            if (!sessionStateToSend && answers.length > 0) {
                // Try to get from the last answer
                const lastAnswer = answers[answers.length - 1][1];
                sessionStateToSend = lastAnswer.sessionState;
            }
            
            const options: ChatAppRequestOptions = {
                context: {
                    overrides: {
                        use_advanced_flow: useAdvancedFlow,
                        top: retrieveCount,
                        retrieval_mode: retrievalMode,
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        temperature: temperature,
                        username: usernameToSend,
                        selected_copilot: selectedCopilotId,
                        user_context: userContext.length > 0 ? userContext : undefined
                    }
                },
                sessionState: sessionStateToSend,
                session_id: sessionId  // Include session ID for conversation context tracking
            };
            
            // Debug logging for session state tracking
            console.log('Session ID:', sessionId);
            console.log('Current sessionState ref:', currentSessionStateRef.current);
            console.log('Session state from last answer:', answers.length > 0 ? answers[answers.length - 1][1].sessionState : 'no previous answers');
            console.log('Sending request with sessionState:', options.sessionState);

            const chatClient: AIChatProtocolClient = new AIChatProtocolClient("/chat");
            let chatResponse: RAGChatCompletion;
            if (shouldStream) {
                const result = (await chatClient.getStreamedCompletion(allMessages, options)) as AsyncIterable<RAGChatCompletionDelta>;
                chatResponse = await handleAsyncRequest(question, answers, result);
                console.log('Final streaming response received');
                console.log('chatResponse:', chatResponse);
                console.log('chatResponse.sessionState:', chatResponse.sessionState);
                console.log('SessionState has last_search_results?', chatResponse.sessionState?.last_search_results?.length || 0);
                console.log('SessionState last_intent:', chatResponse.sessionState?.last_intent);
                // Always update session state, even if undefined
                currentSessionStateRef.current = chatResponse.sessionState || currentSessionStateRef.current;
                setAnswers([...answers, [question, chatResponse]]);
            } else {
                chatResponse = (await chatClient.getCompletion(allMessages, options)) as RAGChatCompletion;
                console.log('Final non-streaming response received');
                console.log('chatResponse:', chatResponse);
                console.log('chatResponse.sessionState:', chatResponse.sessionState);
                console.log('SessionState has last_search_results?', chatResponse.sessionState?.last_search_results?.length || 0);
                console.log('SessionState last_intent:', chatResponse.sessionState?.last_intent);
                // Always update session state, even if undefined
                currentSessionStateRef.current = chatResponse.sessionState || currentSessionStateRef.current;
                setAnswers([...answers, [question, chatResponse]]);
            }

            // Check if this is a follow-up question based on session state
            // A follow-up question is one where:
            // 1. The backend explicitly marks it as follow_up intent, OR
            // 2. We have previous results AND previous answers (indicating ongoing conversation) AND no new data points
            const hasPreviousConversation = answers.length > 0;
            const isFollowUpQuestion = chatResponse.sessionState?.last_intent === "follow_up" || 
                                      (hasPreviousConversation && 
                                       currentSessionStateRef.current?.last_search_results && 
                                       currentSessionStateRef.current?.last_search_results.length > 0 &&
                                       (!chatResponse.context?.data_points || Object.keys(chatResponse.context.data_points).length === 0));
            
            console.log('Is follow-up question:', isFollowUpQuestion);
            console.log('Session state intent:', chatResponse.sessionState?.last_intent);
            console.log('Has previous conversation:', hasPreviousConversation);
            console.log('Has previous results:', currentSessionStateRef.current?.last_search_results?.length || 0);
            console.log('Has new data points:', chatResponse.context?.data_points ? Object.keys(chatResponse.context.data_points).length : 0);
            
            // Check for car results in multiple places: data_points or sessionState.last_search_results
            let carItems: any[] = [];
            let resultsSource = '';
            
            console.log('=== CAR RESULTS CHECK ===');
            console.log('Query:', question);
            console.log('useAdvancedFlow:', useAdvancedFlow);
            console.log('Has context?', !!chatResponse.context);
            console.log('Has context.data_points?', chatResponse.context?.data_points ? Object.keys(chatResponse.context.data_points).length : 0);
            if (chatResponse.context?.data_points && Object.keys(chatResponse.context.data_points).length > 0) {
                console.log('data_points keys:', Object.keys(chatResponse.context.data_points));
                console.log('First data_point sample:', Object.values(chatResponse.context.data_points)[0]);
            }
            console.log('Has sessionState?', !!chatResponse.sessionState);
            console.log('SessionState last_search_results length:', chatResponse.sessionState?.last_search_results?.length || 0);
            console.log('SessionState last_intent:', chatResponse.sessionState?.last_intent);
            console.log('SessionState last_search_query:', chatResponse.sessionState?.last_search_query);
            console.log('=== END CAR RESULTS CHECK ===');
            
            // First check context.data_points
            if (useAdvancedFlow && chatResponse.context && chatResponse.context.data_points && Object.keys(chatResponse.context.data_points).length > 0) {
                const dataPoints = chatResponse.context.data_points;
                console.log('Found data_points with keys:', Object.keys(dataPoints));
                console.log('First data point:', Object.values(dataPoints)[0]);
                carItems = Object.values(dataPoints).map((item: any) => ({
                    ...item,
                    // Ensure all required fields are present
                    id: item.id || item.id_unico,
                    title: item.titulo || `${item.marca} ${item.modelo}`,
                    price: item.precio,
                    marca: item.marca,
                    modelo: item.modelo,
                    year: item.fecha_fabricacion,
                    mileage: item.kilometraje,
                    color: item.color,
                    transmission: item.transmision,
                    fuel_type: item.combustible || item.tipo_combustible,
                    department: item.departamento,
                    description: item.descripcion,
                    platform: item.plataforma,
                    seller_type: item.tipo_vendedor,
                    image_url: item.imagen_url,
                    // Add missing fields for car cards
                    fecha_fabricacion: item.fecha_fabricacion,
                    precio: item.precio,
                    kilometraje: item.kilometraje,
                    transmision: item.transmision,
                    departamento: item.departamento,
                    comentario_experto: item.comentario_experto || item.description || item.descripcion
                }));
                resultsSource = 'data_points';
            }
            // If no data_points but we have sessionState.last_search_results (advanced flow)
            // Check for both 'search' intent and any available results regardless of intent
            else if (useAdvancedFlow && chatResponse.sessionState?.last_search_results && chatResponse.sessionState.last_search_results.length > 0) {
                carItems = chatResponse.sessionState.last_search_results.map((item: any) => ({
                    ...item,
                    // Ensure all required fields are present
                    id: item.id || item.id_unico,
                    title: item.titulo || `${item.marca} ${item.modelo}`,
                    price: item.precio,
                    marca: item.marca,
                    modelo: item.modelo,
                    year: item.fecha_fabricacion,
                    mileage: item.kilometraje,
                    color: item.color,
                    transmission: item.transmision,
                    fuel_type: item.combustible || item.tipo_combustible,
                    department: item.departamento,
                    description: item.descripcion,
                    platform: item.plataforma,
                    seller_type: item.tipo_vendedor,
                    image_url: item.imagen_url,
                    // Add missing fields for car cards
                    fecha_fabricacion: item.fecha_fabricacion,
                    precio: item.precio,
                    kilometraje: item.kilometraje,
                    transmision: item.transmision,
                    departamento: item.departamento,
                    comentario_experto: item.comentario_experto || item.description || item.descripcion
                }));
                resultsSource = 'sessionState';
            }
            
            // Use Advanced RAG results for car listings
            if (carItems.length > 0) {
                console.log(`Using car listings from ${resultsSource}:`, carItems.length, 'items');
                console.log('Sample car item:', carItems[0]); // Debug: show first car structure
                setCarListings(carItems);
                
                // Set appropriate summary message based on results and intent
                const intent = chatResponse.sessionState?.last_intent;
                if (intent === 'search') {
                    setSearchSummary(`Encontré ${carItems.length} vehículo${carItems.length > 1 ? 's' : ''} que coinciden con tu búsqueda.`);
                } else {
                    setSearchSummary(`Mostrando ${carItems.length} vehículo${carItems.length > 1 ? 's' : ''} disponibles.`);
                }
            }
            // If this is a search intent but no results found
            else if (chatResponse.sessionState?.last_intent === 'search' && carItems.length === 0) {
                console.log('Search intent detected but no results found');
                setCarListings([]);
                setSearchSummary("No se encontraron vehículos que coincidan con tu búsqueda. Te sugiero modificar los criterios de búsqueda.");
                
                // Don't attempt fallback search for advanced flow with no results
                // The assistant's response should already handle this case
            }
            // If this is a follow-up question, preserve existing listings
            else if (isFollowUpQuestion) {
                // This is a follow-up question, preserve existing car listings
                console.log('Follow-up question detected, preserving existing car listings');
                // Don't update carListings, keep the existing ones
                // Optionally update the search summary to indicate it's about the same results
                if (carListings.length > 0) {
                    setSearchSummary(`Respondiendo sobre los ${carListings.length} vehículos encontrados anteriormente.`);
                }
            }
            // If no results found anywhere and not a follow-up
            else if (carItems.length === 0 && !isFollowUpQuestion) {
                // For advanced flow with search intent, we already handled empty results above
                // For non-advanced flow, try fallback search
                if (!useAdvancedFlow) {
                    try {
                        console.log('Making fallback car search request for query:', question);
                        const carSearchResponse = await fetch('/cars/search', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                query: question,
                                top: 20
                            })
                        });
                        
                        if (carSearchResponse.ok) {
                            const carSearchResult = await carSearchResponse.json();
                            console.log('Fallback car search result:', carSearchResult);
                            const items = carSearchResult.items || [];
                            setCarListings(items);
                            
                            // Set appropriate summary based on results
                            if (items.length === 0) {
                                setSearchSummary("No se encontraron vehículos que coincidan con tu búsqueda.");
                                // Modify the chat response to reflect no results
                                chatResponse.message.content = "Lo siento, no encontré vehículos que coincidan con tu búsqueda. Te sugiero ajustar los filtros o buscar con otros criterios.";
                            } else {
                                setSearchSummary(carSearchResult.summary || "");
                            }
                        } else {
                            console.error('Fallback car search failed:', carSearchResponse.status);
                            setCarListings([]);
                            setSearchSummary("No se encontraron vehículos disponibles en este momento.");
                            // Modify the chat response to reflect the error
                            chatResponse.message.content = "Lo siento, no pude buscar vehículos en este momento. Por favor, intenta de nuevo más tarde.";
                        }
                    } catch (error) {
                        console.error('Fallback car search error:', error);
                        setCarListings([]);
                        setSearchSummary("No se encontraron vehículos disponibles.");
                        // Modify the chat response to reflect the error
                        chatResponse.message.content = "Lo siento, hubo un problema al buscar vehículos. Por favor, intenta de nuevo.";
                    }
                } else {
                    // For advanced flow, just ensure listings are cleared when no results
                    console.log('Advanced flow with no results - clearing listings');
                    setCarListings([]);
                    // searchSummary should already be set by the condition above
                }
            }
            
            // Always clear searching state after processing response
            setIsSearchingCars(false);

            // Refresh quota status after successful query
            fetchQuotaStatus();

            // Auto-save conversation after each message exchange (if user is signed in)
            if (user?.id) {
                try {
                    let convId = currentConversationId;

                    // Create conversation on first message
                    if (!convId) {
                        // Generate title from first user question (first 50 chars)
                        const title = question.length > 50 ? question.substring(0, 50) + "..." : question;
                        const newConversation = await chatHistoryService.createConversation({ title });
                        convId = newConversation.id;
                        setCurrentConversationId(convId);
                        console.log("Auto-created conversation:", convId);
                    }

                    // Save both user message and assistant response
                    await chatHistoryService.addMessage(convId, { role: "user", content: question });
                    await chatHistoryService.addMessage(convId, { role: "assistant", content: chatResponse.message.content });
                    console.log("Auto-saved messages to conversation:", convId);
                } catch (saveErr) {
                    console.error("Error auto-saving conversation:", saveErr);
                    // Don't fail the chat if save fails - user can still continue chatting
                }
            }

        } catch (e: any) {
            console.error("Chat error:", e);

            // Check if this is a quota exceeded error (429)
            // Handle multiple error formats: response object, message string, or status code
            const is429Error =
                e?.response?.status === 429 ||
                e?.status === 429 ||
                (e?.message && (e.message.includes('429') || e.message.includes('Too Many Requests'))) ||
                (typeof e === 'string' && (e.includes('429') || e.includes('Too Many Requests')));

            if (is429Error) {
                // Try to parse the error detail from various formats
                let errorDetail = null;
                try {
                    errorDetail = e?.response?.data?.detail || e?.detail || e?.body?.detail;

                    // If errorDetail is a string, try to parse it as JSON
                    if (typeof errorDetail === 'string') {
                        try {
                            errorDetail = JSON.parse(errorDetail);
                        } catch {
                            // Not JSON, keep as string
                        }
                    }
                } catch {
                    // Parsing failed
                }

                // Update quota info if we have the details
                if (errorDetail?.error === 'daily_limit_exceeded' || errorDetail?.queries_used !== undefined) {
                    setQuotaInfo({
                        queriesUsed: errorDetail.queries_used || 20,
                        queriesLimit: errorDetail.queries_limit || 20,
                        isUnlimited: false,
                        canQuery: false
                    });
                } else {
                    // Default to showing limit reached even without parsed details
                    setQuotaInfo(prev => ({
                        ...prev,
                        canQuery: false
                    }));
                }

                // Set quota exceeded state for inline display
                setIsQuotaExceeded(true);
                setShowLimitReached(true);
                setIsSearchingCars(false);
                setIsLoading(false);
                // Don't set generic error - we'll show the quota exceeded message instead
                return;
            }

            // For non-429 errors, extract a readable message
            let errorMessage = "Ha ocurrido un error. Por favor, intenta de nuevo.";
            if (e?.message) {
                errorMessage = e.message;
            } else if (typeof e === 'string') {
                errorMessage = e;
            }
            setError(errorMessage);
            setIsSearchingCars(false);
        } finally {
            setIsLoading(false);
        }
    };


    const clearChat = async () => {
        // No need to save here - messages are now auto-saved after each exchange
        lastQuestionRef.current = "";
        currentSessionStateRef.current = null;  // Clear session state
        setSessionId(crypto.randomUUID());  // Generate new session ID for fresh conversation
        error && setError(undefined);
        isQuotaExceeded && setIsQuotaExceeded(false);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        setCarListings([]);
        setIsSearchingCars(false);
        setSelectedCar(null);
        setSearchSummary("");
        setIsCarDetailOpen(false);
        setSelectedCarsForComparison([]);
        setIsComparisonOpen(false);
        setCurrentConversationId(undefined);  // Clear conversation ID to start fresh
        console.log("Chat cleared - new session ID generated");
    };

    // Load a conversation from history
    const loadConversation = useCallback(async (conversationId: string) => {
        try {
            const conversation = await chatHistoryService.getConversation(conversationId);

            // Clear current state
            lastQuestionRef.current = "";
            currentSessionStateRef.current = null;
            setSessionId(crypto.randomUUID());
            setError(undefined);
            setIsQuotaExceeded(false);
            setActiveCitation(undefined);
            setActiveAnalysisPanelTab(undefined);
            setCarListings([]);
            setIsSearchingCars(false);
            setSelectedCar(null);
            setSearchSummary("");
            setIsCarDetailOpen(false);
            setSelectedCarsForComparison([]);
            setIsComparisonOpen(false);

            // Convert messages to answers format
            const loadedAnswers: [string, RAGChatCompletion][] = [];
            const messages = conversation.messages;

            for (let i = 0; i < messages.length; i += 2) {
                const userMsg = messages[i];
                const assistantMsg = messages[i + 1];

                if (userMsg && assistantMsg && userMsg.role === "user" && assistantMsg.role === "assistant") {
                    loadedAnswers.push([
                        userMsg.content,
                        {
                            context: {
                                data_points: {},
                                followup_questions: null,
                                thoughts: []
                            },
                            message: { content: assistantMsg.content, role: "assistant" },
                            sessionState: null
                        }
                    ]);
                }
            }

            if (loadedAnswers.length > 0) {
                lastQuestionRef.current = loadedAnswers[loadedAnswers.length - 1][0];
            }

            setAnswers(loadedAnswers);
            setStreamedAnswers([]);
            setCurrentConversationId(conversationId);
            setIsChatHistoryOpen(false);

            console.log("Conversation loaded:", conversationId);
        } catch (err) {
            console.error("Error loading conversation:", err);
        }
    }, []);

    // Handle selecting a conversation from history
    const handleSelectConversation = useCallback((conversationId: string) => {
        loadConversation(conversationId);
    }, [loadConversation]);

    // Handle creating a new chat from history panel
    const handleNewChatFromHistory = useCallback(async () => {
        await clearChat();
        setIsChatHistoryOpen(false);
    }, [clearChat]);

    // Comparison handlers
    const handleToggleCarSelection = (carId: string) => {
        setSelectedCarsForComparison(prev => {
            if (prev.includes(carId)) {
                return prev.filter(id => id !== carId);
            } else {
                // Limit to 4 cars max for better comparison experience
                if (prev.length >= 4) {
                    return prev;
                }
                return [...prev, carId];
            }
        });
    };

    const handleOpenComparison = () => {
        if (selectedCarsForComparison.length >= 2) {
            setIsComparisonOpen(true);
        }
    };

    const handleCloseComparison = () => {
        setIsComparisonOpen(false);
    };

    const handleClearSelection = () => {
        setSelectedCarsForComparison([]);
    };

    const handleRemoveCarFromComparison = (carId: string) => {
        setSelectedCarsForComparison(prev => {
            const newSelection = prev.filter(id => id !== carId);
            // Close comparison if less than 2 cars remain
            if (newSelection.length < 2) {
                setIsComparisonOpen(false);
            }
            return newSelection;
        });
    };

    const handleAskAIFromComparison = (question: string) => {
        setIsComparisonOpen(false);
        makeApiRequest(question);
    };

    // Get selected car objects for comparison
    const selectedCarsData = selectedCarsForComparison
        .map(id => carListings.find(car => car.id_unico === id))
        .filter(Boolean) as any[];

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);
    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" }), [streamedAnswers]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onTemperatureChange = (
        newValue: number,
        range?: [number, number],
        event?: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent | React.KeyboardEvent
    ) => {
        setTemperature(newValue);
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "15"));
    };

    const onUseAdvancedFlowChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseAdvancedFlow(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };



    const onCarSelect = (car: any) => {
        setSelectedCar(car);
        setIsCarDetailOpen(true);
        setIsSavedCarsOpen(false); // Close saved cars panel when selecting a car
    };

    const onSuggestionClick = (suggestion: string) => {
        makeApiRequest(suggestion);
        // On mobile, switch to chat view after clicking suggestion
        if (isMobile) {
            setShowMobileResults(false);
        }
    };

    const onCarDetailDismiss = () => {
        setIsCarDetailOpen(false);
    };

    const handleCarConversation = (carContext: string) => {
        // Send the car context to the chatbot
        makeApiRequest(carContext);
        // Optionally, you can add a confirmation or acknowledgment
        console.log('Starting conversation about selected car');
    };

    const handleCopilotSelect = (copilotId: string) => {
        setSelectedCopilotId(copilotId);
        // Save to localStorage
        localStorage.setItem('selectedCopilotId', copilotId);
        console.log('Copilot selected:', copilotId);
    };

    const handleCopilotClear = () => {
        setSelectedCopilotId(undefined);
        // Remove from localStorage
        localStorage.removeItem('selectedCopilotId');
        console.log('Copilot mode disabled');
    };

    const selectedCopilot = selectedCopilotId
        ? COPILOTS.find(c => c.id === selectedCopilotId) || null
        : null;

    // Map copilot IDs to their empty state logo images
    const getCopilotEmptyStateLogo = () => {
        if (!selectedCopilotId) return "/firulAI.png";

        const logoMap: Record<string, string> = {
            "firulai": "/firulAI.png",
            "profe-ahorro": "/genio_num.png",
            "don-trato": "/revendedor.png",
            "pofi": "/elpofiv2.png"
        };

        return logoMap[selectedCopilotId] || "/firulAI.png";
    };

    const onMobileToggle = () => {
        setShowMobileResults(!showMobileResults);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    return (
        <>
            <div className={`${styles.container} ${isConfigPanelOpen ? styles.containerWithPanel : ''}`}>
            <Sidebar
                onClearChat={clearChat}
                isClearChatDisabled={!lastQuestionRef.current || isLoading}
                onCopilotClick={() => setIsCopilotSelectorOpen(true)}
                onCopilotClear={handleCopilotClear}
                selectedCopilot={selectedCopilot}
                onContextClick={() => setIsContextModalOpen(true)}
                onSavedCarsClick={() => setIsSavedCarsOpen(true)}
                savedCarsCount={savedCarsCount}
                onOportunidadesClick={() => setIsOportunidadesOpen(true)}
                onHistoryClick={() => setIsChatHistoryOpen(true)}
                quotaInfo={quotaInfo}
                justUpgraded={justUpgraded}
                onUpgradeClick={handleUpgradeClick}
            />
            {/* Desktop Layout - Use ResizableSplitter */}
            <div className={styles.desktopLayout}>
                <ResizableSplitter
                    leftPanel={
                        <div className={styles.chatRoot}>
                            <div className={styles.chatContainer}>
                                {!lastQuestionRef.current ? (
                                    <div className={styles.chatEmptyState}>
                                        <img
                                            src={getCopilotEmptyStateLogo()}
                                            alt={selectedCopilot?.name || "Videntis logo"}
                                            className={styles.chatEmptyStateLogo}
                                        />
                                        <p className={styles.chatEmptyStateTagline}>
                                            Describe tu auto ideal y encuentra las mejores opciones.
                                        </p>
                                    </div>
                                ) : null}

                                {lastQuestionRef.current && (
                                    <div className={styles.chatMessageStream}>
                                        {isStreaming &&
                                            streamedAnswers.map((streamedAnswer, index) => (
                                                <div key={index}>
                                                    <UserChatMessage message={streamedAnswer[0]} />
                                                    <div className={styles.chatMessageGpt}>
                                                        <Answer
                                                            isStreaming={true}
                                                            key={index}
                                                            answer={streamedAnswer[1]}
                                                            isSelected={false}
                                                            onCitationClicked={c => onShowCitation(c, index)}
                                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                            onCarClick={onCarSelect}
                                                            onShowPanelClick={onMobileToggle}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        {!isStreaming &&
                                            answers.map((answer, index) => (
                                                <div key={index}>
                                                    <UserChatMessage message={answer[0]} />
                                                    <div className={styles.chatMessageGpt}>
                                                        <Answer
                                                            isStreaming={false}
                                                            key={index}
                                                            answer={answer[1]}
                                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                            onCitationClicked={c => onShowCitation(c, index)}
                                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                            onCarClick={onCarSelect}
                                                            onShowPanelClick={onMobileToggle}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        {isLoading && (
                                            <>
                                                <UserChatMessage message={lastQuestionRef.current} />
                                                <div className={styles.chatMessageGptMinWidth}>
                                                    <AnswerLoading />
                                                </div>
                                            </>
                                        )}
                                        {isQuotaExceeded ? (
                                            <>
                                                <UserChatMessage message={lastQuestionRef.current} />
                                                <div className={styles.chatMessageGptMinWidth}>
                                                    <QuotaExceededMessage
                                                        queriesUsed={quotaInfo.queriesUsed}
                                                        queriesLimit={quotaInfo.queriesLimit || 20}
                                                        onUpgrade={() => setIsUpgradeModalOpen(true)}
                                                    />
                                                </div>
                                            </>
                                        ) : error ? (
                                            <>
                                                <UserChatMessage message={lastQuestionRef.current} />
                                                <div className={styles.chatMessageGptMinWidth}>
                                                    <AnswerError
                                                        error={typeof error === 'string' ? error : "Ha ocurrido un error. Por favor, intenta de nuevo."}
                                                        onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                                    />
                                                </div>
                                            </>
                                        ) : null}
                                        <div ref={chatMessageStreamEnd} />
                                    </div>
                                )}

                                {/* Enhanced chat advisor modes */}

                                <div className={!lastQuestionRef.current ? styles.chatInputCentered : styles.chatInput}>
                                    <p className={styles.chatDisclaimer}>
                                        Analizamos cientos de listados para mostrarte solo las mejores opciones.
                                    </p>
                                    <QuestionInput
                                        clearOnSend
                                        placeholder="El Auto que quiero es..."
                                        disabled={isLoading}
                                        onSend={question => makeApiRequest(question)}
                                    />
                                </div>
                            </div>

                            {SHOW_ANALYSIS_PANEL && answers.length > 0 && activeAnalysisPanelTab && (
                                <AnalysisPanel
                                    className={styles.chatAnalysisPanel}
                                    activeCitation={activeCitation}
                                    onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                                    citationHeight="810px"
                                    answer={answers[selectedAnswer][1]}
                                    activeTab={activeAnalysisPanelTab}
                                />
                            )}
                        </div>
                    }
                    rightPanel={
                        <div className={styles.resultsPanel}>
                            <ResultsPanel
                                listings={carListings}
                                isLoading={isSearchingCars}
                                onCarSelect={onCarSelect}
                                onSuggestionClick={onSuggestionClick}
                                isEmpty={carListings.length === 0 && !isSearchingCars}
                                searchSummary={searchSummary}
                                selectedCars={selectedCarsForComparison}
                                onToggleSelection={handleToggleCarSelection}
                                savedCarIds={savedCarIds}
                            />
                        </div>
                    }
                />
            </div>

            {/* Mobile Layout - Simple full-screen views */}
            <div className={styles.mobileLayout}>
                {/* Mobile Sidebar Panel */}
                <Panel
                    isOpen={showMobileSidebar}
                    onDismiss={() => setShowMobileSidebar(false)}
                    type={PanelType.smallFixedNear}
                    hasCloseButton={true}
                    styles={{
                        main: {
                            background: '#2d2d2d',
                            borderRight: '1px solid #444',
                            width: '280px',
                            maxWidth: '80vw'
                        },
                        content: {
                            padding: 0,
                            background: '#2d2d2d'
                        },
                        scrollableContent: {
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            background: '#2d2d2d',
                            // CRITICAL: Enable touch scrolling on mobile
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: 'pan-y',
                            overscrollBehaviorY: 'contain'
                        },
                        closeButton: {
                            color: '#ffffff',
                            backgroundColor: '#20B2AA',
                            borderRadius: '4px',
                            selectors: {
                                ':hover': {
                                    backgroundColor: '#1a9a94',
                                    color: '#ffffff'
                                }
                            }
                        },
                        overlay: {
                            backgroundColor: 'rgba(0, 0, 0, 0.4)'
                        }
                    }}
                >
                    <div className={styles.mobileSidebarPanel}>
                        <Sidebar
                            className="sidebarMobilePanel"
                            onClearChat={async () => {
                                await clearChat();
                                setShowMobileSidebar(false);
                            }}
                            isClearChatDisabled={!lastQuestionRef.current || isLoading}
                            onCopilotClick={() => {
                                setShowMobileSidebar(false);
                                setIsCopilotSelectorOpen(true);
                            }}
                            onCopilotClear={() => {
                                handleCopilotClear();
                                setShowMobileSidebar(false);
                            }}
                            selectedCopilot={selectedCopilot}
                            onContextClick={() => {
                                setShowMobileSidebar(false);
                                setIsContextModalOpen(true);
                            }}
                            onSavedCarsClick={() => {
                                setShowMobileSidebar(false);
                                setIsSavedCarsOpen(true);
                            }}
                            savedCarsCount={savedCarsCount}
                            onOportunidadesClick={() => {
                                setShowMobileSidebar(false);
                                setIsOportunidadesOpen(true);
                            }}
                            onHistoryClick={() => {
                                setShowMobileSidebar(false);
                                setIsChatHistoryOpen(true);
                            }}
                            quotaInfo={quotaInfo}
                            justUpgraded={justUpgraded}
                            onUpgradeClick={() => {
                                setShowMobileSidebar(false);
                                handleUpgradeClick();
                            }}
                        />
                    </div>
                </Panel>

                {!showMobileResults ? (
                    // Mobile Chat View
                    <div className={styles.mobileChatView}>
                        {/* Mobile Sidebar Toggle - positioned at top left */}
                        <MobileSidebarToggle
                            onToggle={() => setShowMobileSidebar(true)}
                            savedCarsCount={savedCarsCount}
                        />
                        <div className={styles.chatContainer}>
                            {!lastQuestionRef.current ? (
                                <div className={styles.chatEmptyState}>
                                    <img
                                        src={getCopilotEmptyStateLogo()}
                                        alt={selectedCopilot?.name || "Videntis logo"}
                                        className={styles.chatEmptyStateLogo}
                                    />
                                    <p className={styles.chatEmptyStateTagline}>
                                        Describe tu auto ideal y encuentra las mejores opciones.
                                    </p>
                                </div>
                            ) : null}

                            {lastQuestionRef.current ? (
                                <div className={styles.chatMessageStream}>
                                    {isStreaming &&
                                        streamedAnswers.map((streamedAnswer, index) => (
                                            <div key={index}>
                                                <UserChatMessage message={streamedAnswer[0]} />
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        isStreaming={true}
                                                        key={index}
                                                        answer={streamedAnswer[1]}
                                                        isSelected={false}
                                                        onCitationClicked={c => onShowCitation(c, index)}
                                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        onCarClick={onCarSelect}
                                                        onShowPanelClick={onMobileToggle}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    {!isStreaming &&
                                        answers.map((answer, index) => (
                                            <div key={index}>
                                                <UserChatMessage message={answer[0]} />
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        isStreaming={false}
                                                        key={index}
                                                        answer={answer[1]}
                                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                        onCitationClicked={c => onShowCitation(c, index)}
                                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        onCarClick={onCarSelect}
                                                        onShowPanelClick={onMobileToggle}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    {isLoading && (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerLoading />
                                            </div>
                                        </>
                                    )}
                                    {isQuotaExceeded ? (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <QuotaExceededMessage
                                                    queriesUsed={quotaInfo.queriesUsed}
                                                    queriesLimit={quotaInfo.queriesLimit || 20}
                                                    onUpgrade={() => setIsUpgradeModalOpen(true)}
                                                />
                                            </div>
                                        </>
                                    ) : error ? (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerError
                                                    error={typeof error === 'string' ? error : "Ha ocurrido un error. Por favor, intenta de nuevo."}
                                                    onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                                />
                                            </div>
                                        </>
                                    ) : null}
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            ) : null}


                            <div className={!lastQuestionRef.current ? styles.chatInputCentered : styles.chatInput}>
                                <p className={styles.chatDisclaimer}>
                                    Analizamos cientos de listados para mostrarte solo las mejores opciones.
                                </p>
                                <QuestionInput
                                    clearOnSend
                                    placeholder="El Auto que quiero es..."
                                    disabled={isLoading}
                                    onSend={question => makeApiRequest(question)}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    // Mobile Results View
                    <div className={styles.mobileResultsView}>
                        {/* Mobile Sidebar Toggle - also show in results view */}
                        <MobileSidebarToggle
                            onToggle={() => setShowMobileSidebar(true)}
                            savedCarsCount={savedCarsCount}
                        />
                        <ResultsPanel
                            listings={carListings}
                            isLoading={isSearchingCars}
                            onCarSelect={onCarSelect}
                            onSuggestionClick={onSuggestionClick}
                            isEmpty={carListings.length === 0 && !isSearchingCars}
                            searchSummary={searchSummary}
                            selectedCars={selectedCarsForComparison}
                            onToggleSelection={handleToggleCarSelection}
                            savedCarIds={savedCarIds}
                        />
                    </div>
                )}
            </div>

            {/* Comparison FAB */}
            <ComparisonFAB
                selectedCount={selectedCarsForComparison.length}
                onCompare={handleOpenComparison}
                onClear={handleClearSelection}
            />

            {/* Comparison View */}
            <CarComparisonView
                cars={selectedCarsData}
                isOpen={isComparisonOpen}
                onDismiss={handleCloseComparison}
                onRemoveCar={handleRemoveCarFromComparison}
                onAskAI={handleAskAIFromComparison}
            />

            {/* Mobile Toggle Button */}
            <MobileToggle
                showResults={showMobileResults}
                onToggle={onMobileToggle}
                resultsCount={carListings.length}
                className={carListings.length > 0 || isSearchingCars ? '' : 'hide-when-empty'}
            />
            
            <Panel
                headerText="Configure answer generation"
                isOpen={isConfigPanelOpen}
                isBlocking={false}
                type={PanelType.medium}
                onDismiss={() => setIsConfigPanelOpen(false)}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                isFooterAtBottom={true}
                isLightDismiss={false}
                styles={{
                    main: {
                        background: '#2d2d2d',
                        borderLeft: '1px solid #444'
                    },
                    header: {
                        background: '#2d2d2d',
                        borderBottom: '1px solid #444'
                    },
                    headerText: {
                        color: '#ffffff'
                    },
                    content: {
                        background: '#2d2d2d',
                        color: '#ffffff'
                    }
                }}
            >

                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useAdvancedFlow}
                    label="Use advanced flow with query rewriting and filter formulation. Not compatible with Ollama models."
                    onChange={onUseAdvancedFlowChange}
                />

                <h3>Settings for database search:</h3>

                <SpinButton
                    className={styles.chatSettingsSeparator}
                    label="Retrieve this many matching rows:"
                    min={1}
                    max={50}
                    defaultValue={retrieveCount.toString()}
                    onChange={onRetrieveCountChange}
                />

                <VectorSettings updateRetrievalMode={(retrievalMode: RetrievalMode) => setRetrievalMode(retrievalMode)} />

                <h3>Settings for final chat completion:</h3>

                <TextField
                    className={styles.chatSettingsSeparator}
                    defaultValue={promptTemplate}
                    label="Override prompt template"
                    multiline
                    autoAdjustHeight
                    onChange={onPromptTemplateChange}
                    styles={{
                        field: {
                            background: '#3d3d3d',
                            border: '1px solid #666',
                            color: '#ffffff'
                        },
                        fieldGroup: {
                            border: '1px solid #666',
                            background: '#3d3d3d'
                        }
                    }}
                />

                <Slider
                    className={styles.chatSettingsSeparator}
                    label="Temperature"
                    min={0}
                    max={1}
                    step={0.1}
                    defaultValue={temperature}
                    onChange={onTemperatureChange}
                    showValue
                    snapToStep
                    styles={{
                        slideBox: {
                            background: 'transparent',
                            position: 'relative'
                        },
                        line: {
                            background: '#666',
                            height: '4px'
                        },
                        activeSection: {
                            background: '#20B2AA',
                            height: '4px'
                        },
                        inactiveSection: {
                            background: '#666',
                            height: '4px'
                        },
                        thumb: {
                            background: '#20B2AA',
                            border: '2px solid #20B2AA',
                            borderRadius: '50%',
                            zIndex: 10
                        },
                        valueLabel: {
                            background: '#2d2d2d',
                            color: '#ffffff',
                            border: '1px solid #666',
                            zIndex: 20,
                            position: 'relative'
                        }
                    }}
                />

                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={shouldStream}
                    label="Stream chat completion responses"
                    onChange={onShouldStreamChange}
                />
            </Panel>

            <CarDetailDrawer
                car={selectedCar}
                isOpen={isCarDetailOpen}
                onDismiss={onCarDetailDismiss}
                onConversarClick={handleCarConversation}
            />

            <Panel
                isOpen={isSavedCarsOpen}
                onDismiss={() => setIsSavedCarsOpen(false)}
                type={PanelType.custom}
                customWidth="100vw"
                headerText=""
                hasCloseButton={false}
                styles={{
                    main: { backgroundColor: '#0a0a0f' },
                    content: { padding: 0 },
                    scrollableContent: { padding: 0, height: '100vh', display: 'flex', flexDirection: 'column' },
                    navigation: { display: 'none' },
                    contentInner: { display: 'flex', height: '100vh' },
                    commands: { display: 'none' }
                }}
            >
                <SavedCars
                    onSelectCar={onCarSelect}
                    onClose={() => setIsSavedCarsOpen(false)}
                />
            </Panel>

            <Panel
                isOpen={isOportunidadesOpen}
                onDismiss={() => setIsOportunidadesOpen(false)}
                type={PanelType.custom}
                customWidth="100vw"
                headerText=""
                hasCloseButton={false}
                styles={{
                    main: { backgroundColor: '#f8f9fa' },
                    content: { padding: 0 },
                    scrollableContent: { padding: 0 },
                    navigation: { display: 'none' },
                    contentInner: { display: 'flex', height: '100vh' },
                    commands: { display: 'none' }
                }}
            >
                <Oportunidades
                    onSelectCar={onCarSelect}
                    onClose={() => setIsOportunidadesOpen(false)}
                />
            </Panel>
            </div>

            {/* Modals rendered outside container to avoid padding offset */}
            <CopilotSelector
                isOpen={isCopilotSelectorOpen}
                onDismiss={() => setIsCopilotSelectorOpen(false)}
                onCopilotSelect={handleCopilotSelect}
                selectedCopilotId={selectedCopilotId}
            />

            <ContextModal
                isOpen={isContextModalOpen}
                onDismiss={() => setIsContextModalOpen(false)}
                onSubmit={(context) => {
                    setUserContext(context);
                    console.log("User context set:", context);
                }}
                initialContext={userContext}
            />

            <UpgradePlanModal
                isOpen={isUpgradeModalOpen}
                onDismiss={() => {
                    setIsUpgradeModalOpen(false);
                    setShowLimitReached(false);
                }}
                onUpgrade={handleUpgrade}
                queriesUsed={quotaInfo.queriesUsed}
                queriesLimit={quotaInfo.queriesLimit || 20}
                showLimitReached={showLimitReached}
            />

            <ChatHistory
                isOpen={isChatHistoryOpen}
                onClose={() => setIsChatHistoryOpen(false)}
                onSelectConversation={handleSelectConversation}
                onNewChat={handleNewChatFromHistory}
                currentConversationId={currentConversationId}
            />
        </>
    );
};

export default Chat;
